/**
 * Store de layout del Dashboard — orden de los KPIs por usuario (paquete AF).
 *
 * Cada usuario tiene su propio orden persistido en localStorage. La key
 * `riga-traza-dashboard-layout-v1` guarda un map `userId → string[]`.
 *
 * El "orden default" es el de `DASHBOARD_KPIS_DEFAULT`. Si el usuario nunca
 * dragueó, devuelve el default. Si el usuario tiene un orden persistido,
 * lo respeta (filtrando IDs desconocidos y agregando los nuevos al final
 * para resiliencia a cambios en el set de KPIs).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/** IDs canónicos de los KPIs del Dashboard. Mantener sync con DashboardPage. */
export const DASHBOARD_KPIS_DEFAULT = [
  'remitos_por_trazar',
  'en_transito',
  'errores_anmat',
  'errores_cierre_erp',
  'series_en_stock',
] as const;

export type DashboardKpiId = (typeof DASHBOARD_KPIS_DEFAULT)[number];

interface DashboardLayoutState {
  /** Orden persistido por usuario. */
  ordenPorUsuario: Record<string, DashboardKpiId[]>;

  /** Devuelve el orden actual del usuario (default si no se persistió). */
  getOrden: (usuarioId: string | null) => DashboardKpiId[];

  /** Setea el orden completo para un usuario. */
  setOrden: (usuarioId: string, orden: DashboardKpiId[]) => void;

  /** Reinicia al orden default para un usuario. */
  reset: (usuarioId: string) => void;
}

function reconciliarOrden(
  guardado: readonly DashboardKpiId[] | undefined,
): DashboardKpiId[] {
  if (!guardado || guardado.length === 0) return [...DASHBOARD_KPIS_DEFAULT];
  // Quitar ids desconocidos (en caso de que se hayan removido KPIs)
  const knownSet = new Set<DashboardKpiId>(DASHBOARD_KPIS_DEFAULT);
  const filtrado = guardado.filter((id): id is DashboardKpiId => knownSet.has(id));
  // Agregar al final cualquier id default que no esté (KPIs nuevos)
  const enFiltrado = new Set(filtrado);
  for (const id of DASHBOARD_KPIS_DEFAULT) {
    if (!enFiltrado.has(id)) filtrado.push(id);
  }
  return filtrado;
}

export const useDashboardLayoutStore = create<DashboardLayoutState>()(
  persist(
    (set, get) => ({
      ordenPorUsuario: {},

      getOrden: (usuarioId) => {
        if (!usuarioId) return [...DASHBOARD_KPIS_DEFAULT];
        return reconciliarOrden(get().ordenPorUsuario[usuarioId]);
      },

      setOrden: (usuarioId, orden) => {
        set((state) => ({
          ordenPorUsuario: { ...state.ordenPorUsuario, [usuarioId]: orden },
        }));
      },

      reset: (usuarioId) => {
        set((state) => {
          const next = { ...state.ordenPorUsuario };
          delete next[usuarioId];
          return { ordenPorUsuario: next };
        });
      },
    }),
    {
      name: 'riga-traza-dashboard-layout-v1',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
